// TimelineSection.js - VERSION COMPLÈTE CORRIGÉE
import { AlertCircle, Target, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { TimelineContent } from './TimelineComponents';
import TimelineFilters from './TimelineFilters';

const TimelineSection = ({
    showFilters = false,
    filterByYear = 'all',
    filterByCategory = 'all',
    filterByObjective = 'all',
    filterByPlanned = 'all',
    filterByStatus = 'all',
    onFilterReset,
    onFilterYearChange,
    onFilterCategoryChange,
    onFilterObjectiveChange,
    onFilterPlannedChange,
    onFilterStatusChange,
    onGlobalRefresh,
    data,
    hooks,
    updateTaskStatus
}) => {
    // ⚡ PLUS D'APPELS À useTimelineDataCached - on utilise les données passées en props
    const {
        objectifs = [],
        plans = [],
        isLoading = false,
        hasError = false,
        errors = {},
        isFromCache = false
    } = data || {};

    // Transformer les données au format timeline SANS faire d'appels API
    const timelineData = useMemo(() => {
        const timelineItems = [];

        // Créer un mapping des objectifs pour accès rapide
        const objectifsMap = new Map();
        objectifs.forEach(objectif => {
            if (objectif.id_objectif) {
                objectifsMap.set(objectif.id_objectif, objectif);
            }
        });

        // Fonction pour mapper les statuts d'affichage
        const mapStatusDisplay = (status) => {
            const statusMapping = {
                'a_faire': 'À faire',
                'en_cours': 'En cours',
                'fait': 'Fait',
                'annule': 'Annulé'
            };
            return statusMapping[status] || status;
        };

        // Fonction pour créer un milestone
        const createMilestone = (item, type) => {
            let title, objectiveName, status, value, subtasks = [], dueDate, tag;

            const getObjectiveInfo = (objectifId) => {
                const objectif = objectifsMap.get(objectifId);
                if (objectif) {
                    return {
                        name: objectif.title || 'Objectif',
                        category: objectif.category || null
                    };
                }
                return { name: 'Objectif', category: null };
            };

            switch (type) {
                case 'objective':
                    title = item.title;
                    objectiveName = item.category || 'Objectif';
                    status = mapStatusDisplay(item.status);
                    value = `${(item.current_value || 0).toLocaleString('fr-FR')}/${(item.target_value || 0).toLocaleString('fr-FR')} ${item.value_unit || ''}`;
                    dueDate = item.target_date ? new Date(item.target_date) : null;
                    subtasks = [];
                    tag = item.category;
                    break;

                case 'task':
                    title = item.title;
                    const objectiveInfo = getObjectiveInfo(item.planObjectiveId);
                    objectiveName = objectiveInfo.name;
                    status = mapStatusDisplay(item.status);
                    value = item.estimated_hours ? `${item.estimated_hours}h` : '';
                    dueDate = item.deadline ? new Date(item.deadline) : null;
                    subtasks = (item.subtasks || []).map(subtask => ({
                        title: subtask.title,
                        status: mapStatusDisplay(subtask.status)
                    }));
                    tag = objectiveName;
                    break;

                default:
                    title = item.title || 'Élément';
                    objectiveName = 'Non défini';
                    status = mapStatusDisplay(item.status || 'a_faire');
                    value = '';
                    dueDate = null;
                    subtasks = [];
                    tag = null;
                    break;
            }

            return {
                id: `${type}-${item.id_objectif || item.id_plan || item.id_tache || item.id}`,
                title,
                objectiveName,
                status,
                value,
                subtasks,
                dueDate,
                type,
                data: item,
                tag,
                isFromCache: isFromCache
            };
        };

        // Ajouter les objectifs
        objectifs.forEach(objectif => {
            timelineItems.push(createMilestone(objectif, 'objective'));
        });

        // Ajouter SEULEMENT les tâches des plans
        plans.forEach(plan => {
            if (plan.tasks) {
                let tasks = [];

                if (typeof plan.tasks === 'string') {
                    try {
                        tasks = JSON.parse(plan.tasks);
                    } catch (e) {
                        console.warn('Erreur parsing tasks JSON:', e);
                        tasks = [];
                    }
                } else if (Array.isArray(plan.tasks)) {
                    tasks = plan.tasks;
                }

                tasks.forEach((task, taskIndex) => {
                    const taskWithPlanInfo = {
                        ...task,
                        planObjectiveId: plan.id_objectif,
                        id_plan: plan.id_plan,
                        task_index: taskIndex,
                        id: task.id || `task-${plan.id_plan}-${taskIndex}`
                    };
                    
                    timelineItems.push(createMilestone(taskWithPlanInfo, 'task'));
                });
            }
        });

        // Grouper par mois/année
        const groupedByMonth = {};

        timelineItems.forEach(item => {
            let monthKey;

            if (item.dueDate) {
                const date = item.dueDate;
                const monthNames = [
                    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
                    'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
                ];
                monthKey = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            } else {
                monthKey = 'À planifier';
            }

            if (!groupedByMonth[monthKey]) {
                groupedByMonth[monthKey] = [];
            }
            groupedByMonth[monthKey].push(item);
        });

        // Convertir en format timeline et trier
        const result = Object.entries(groupedByMonth)
            .map(([month, milestones]) => ({
                month,
                milestones: milestones.sort((a, b) => {
                    if (a.dueDate && b.dueDate) return a.dueDate - b.dueDate;
                    if (a.dueDate && !b.dueDate) return -1;
                    if (!a.dueDate && b.dueDate) return 1;

                    const typeOrder = { objective: 0, task: 2 };
                    return (typeOrder[a.type] || 4) - (typeOrder[b.type] || 4);
                })
            }))
            .sort((a, b) => {
                if (a.month === 'À planifier') return 1;
                if (b.month === 'À planifier') return -1;

                const parseMonth = (monthStr) => {
                    const [monthName, year] = monthStr.split(' ');
                    const monthMap = {
                        'Jan': 0, 'Fév': 1, 'Mar': 2, 'Avr': 3, 'Mai': 4, 'Jun': 5,
                        'Jul': 6, 'Aoû': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Déc': 11
                    };
                    return new Date(parseInt(year), monthMap[monthName] || 0);
                };

                return parseMonth(a.month) - parseMonth(b.month);
            });

        return result;
    }, [objectifs, plans, isFromCache]);

    // Fonction utilitaire pour trouver la catégorie d'une tâche
    const getTaskCategory = (task, allTimelineData) => {
        const objectiveName = task.tag;

        for (const monthData of allTimelineData) {
            const objective = monthData.milestones.find(milestone =>
                milestone.type === 'objective' && milestone.title === objectiveName
            );
            if (objective && objective.data && objective.data.category) {
                return objective.data.category;
            }
        }
        return null;
    };

    // Calculer les données disponibles pour les filtres
    const availableYears = useMemo(() => {
        const years = timelineData
            .filter(item => item.month !== 'À planifier')
            .map(item => item.month.split(' ')[1])
            .filter(Boolean);
        return [...new Set(years)].sort();
    }, [timelineData]);

    const availableTaskCategories = useMemo(() => {
        const categories = new Set();
        timelineData.forEach(monthData => {
            monthData.milestones.forEach(milestone => {
                if (milestone.type === 'task') {
                    const taskCategory = getTaskCategory(milestone, timelineData);
                    if (taskCategory) {
                        categories.add(taskCategory);
                    }
                } else if (milestone.type === 'objective' && milestone.data && milestone.data.category) {
                    categories.add(milestone.data.category);
                }
            });
        });
        return Array.from(categories);
    }, [timelineData]);

    const availableTaskObjectives = useMemo(() => {
        const objectives = new Set();
        timelineData.forEach(monthData => {
            monthData.milestones.forEach(milestone => {
                if (milestone.type === 'task') {
                    const objectiveName = milestone.tag || milestone.objectiveName || milestone.objective;
                    if (objectiveName) {
                        objectives.add(objectiveName);
                    }
                }
            });
        });
        return Array.from(objectives);
    }, [timelineData]);

    const availableStatuses = useMemo(() => {
        const statuses = timelineData.flatMap(item =>
            item.milestones
                .filter(milestone => milestone.type === 'task')
                .map(milestone => milestone.status)
        ).filter(Boolean);

        const allPossibleStatuses = ['À faire', 'En cours', 'Fait', 'Annulé'];
        const uniqueStatuses = [...new Set([...statuses, ...allPossibleStatuses])];

        return uniqueStatuses.sort();
    }, [timelineData]);

    const availablePlannedStates = useMemo(() => {
        const states = [];
        let hasPlanned = false;
        let hasUnplanned = false;

        timelineData.forEach(item => {
            item.milestones.forEach(milestone => {
                if (milestone.type === 'task') {
                    const hasDate = milestone.dueDate !== null && milestone.dueDate !== undefined;
                    const isInPlannedSection = item.month !== 'À planifier';

                    if (hasDate && isInPlannedSection) {
                        hasPlanned = true;
                    } else {
                        hasUnplanned = true;
                    }
                }
            });
        });

        if (hasPlanned) states.push('planned');
        if (hasUnplanned) states.push('unplanned');

        return states;
    }, [timelineData]);

    // Filtrage des données
    const filteredTimelineData = useMemo(() => {
        return timelineData.map(monthData => {
            // FILTRAGE PAR ANNÉE
            if (filterByYear !== 'all') {
                if (monthData.month === 'À planifier') {
                    return null;
                } else {
                    const monthParts = monthData.month.split(' ');
                    if (monthParts.length >= 2) {
                        const year = monthParts[1];
                        if (year !== filterByYear) {
                            return null;
                        }
                    }
                }
            }

            // Filtrer pour ne garder QUE les tâches principales
            const filteredMilestones = monthData.milestones.filter(milestone => {
                if (milestone.type !== 'task') {
                    return false;
                }

                // FILTRAGE PAR CATÉGORIE
                if (filterByCategory !== 'all') {
                    const taskCategory = getTaskCategory(milestone, timelineData);
                    if (!taskCategory || taskCategory !== filterByCategory) {
                        return false;
                    }
                }

                // FILTRAGE PAR OBJECTIF
                if (filterByObjective !== 'all') {
                    const taskObjective = milestone.tag || milestone.objectiveName || milestone.objective;
                    if (!taskObjective || taskObjective !== filterByObjective) {
                        return false;
                    }
                }

                // FILTRAGE PAR STATUT
                if (filterByStatus !== 'all') {
                    if (!milestone.status || milestone.status !== filterByStatus) {
                        return false;
                    }
                }

                // FILTRAGE PAR PLANIFICATION
                if (filterByPlanned !== 'all') {
                    const hasDate = milestone.dueDate !== null && milestone.dueDate !== undefined;
                    const isInPlannedSection = monthData.month !== 'À planifier';

                    if (filterByPlanned === 'planned') {
                        if (!hasDate || !isInPlannedSection) {
                            return false;
                        }
                    } else if (filterByPlanned === 'unplanned') {
                        if (hasDate && isInPlannedSection) {
                            return false;
                        }
                    }
                }

                return true;
            });

            if (filteredMilestones.length > 0) {
                return {
                    ...monthData,
                    milestones: filteredMilestones
                };
            }

            return null;
        }).filter(Boolean);
    }, [timelineData, filterByYear, filterByCategory, filterByObjective, filterByPlanned, filterByStatus]);

    // Vérification de sécurité
    if (!data || !hooks) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-center text-gray-500">
                    <p>Données non disponibles</p>
                    <p className="text-sm mt-2">Veuillez recharger la page</p>
                </div>
            </div>
        );
    }

    // Handler pour rafraîchissement
    const handleTimelineRefresh = () => {
        if (onGlobalRefresh) {
            onGlobalRefresh();
        }
    };

    // Gestion des états de chargement
    if (isLoading && timelineData.length === 0) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Chargement de la timeline...</p>
                </div>
            </div>
        );
    }

    // Gestion des erreurs
    if (hasError) {
        const error = errors.objectifs || errors.plans || 'Erreur inconnue';
        return (
            <div className="h-full flex items-center justify-center p-6">
                <div className="text-center bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                    <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-red-800 mb-2">Erreur de chargement</h3>
                    <p className="text-sm text-red-600 mb-4">{error?.message || error}</p>
                    <button
                        onClick={handleTimelineRefresh}
                        className="px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {/* Filtres - hauteur fixe */}
            {showFilters && (
                <div className="flex-shrink-0">
                    <TimelineFilters
                        filterByYear={filterByYear}
                        filterByCategory={filterByCategory}
                        filterByObjective={filterByObjective}
                        filterByPlanned={filterByPlanned}
                        filterByStatus={filterByStatus}
                        availableYears={availableYears}
                        availableCategories={availableTaskCategories}
                        availableObjectives={availableTaskObjectives}
                        availableStatuses={availableStatuses}
                        availablePlannedStates={availablePlannedStates}
                        onFilterYearChange={onFilterYearChange}
                        onFilterCategoryChange={onFilterCategoryChange}
                        onFilterObjectiveChange={onFilterObjectiveChange}
                        onFilterPlannedChange={onFilterPlannedChange}
                        onFilterStatusChange={onFilterStatusChange}
                        onReset={onFilterReset}
                        resultCount={filteredTimelineData.reduce((total, month) => total + month.milestones.length, 0)}
                    />
                </div>
            )}

            {/* Zone de scroll pour la timeline */}
            <div className="flex-1 overflow-auto">
                <div className="px-6 py-4">
                    {filteredTimelineData.length === 0 ? (
                        <div className="flex items-center justify-center py-20 text-gray-500">
                            <div className="text-center">
                                <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <div className="text-lg font-medium mb-2">
                                    {timelineData.length === 0 ? 'Aucune tâche disponible' : 'Aucune tâche trouvée'}
                                </div>
                                <div className="text-sm mb-4">
                                    {timelineData.length === 0
                                        ? 'Créez des objectifs et des plans d\'action pour voir vos tâches'
                                        : 'Essayez de modifier vos filtres pour voir plus de tâches'
                                    }
                                </div>
                                {timelineData.length > 0 && (
                                    <button
                                        onClick={onFilterReset}
                                        className="px-4 py-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-sm"
                                    >
                                        Réinitialiser les filtres
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <TimelineContent
                            timelineData={filteredTimelineData}
                            updateTaskStatus={updateTaskStatus}
                            hooks={hooks}
                            onRefresh={handleTimelineRefresh}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimelineSection;